<template>
  <div class="sc-public">

    <!-- ═══════ NAVBAR ═══════ -->
    <nav class="sc-nav" :class="{ 'sc-nav--scrolled': scrolled }">
      <div class="sc-container sc-nav__inner">
        <router-link class="sc-nav__logo" to="/">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" :alt="(company && company.name) || 'Structurecity'" />
          <span v-else class="sc-nav__logo-text">Structure<span>city</span></span>
        </router-link>

        <div class="sc-nav__links" :class="{ 'sc-nav__links--open': menuOpen }">
          <div class="sc-nav__mobile-search">
            <input
              class="sc-mobile__search-input"
              type="search"
              placeholder="Search…"
              v-model="searchQuery"
              @keydown.enter="doSearch"
            />
            <button class="sc-mobile__search-btn" @click="doSearch" aria-label="Search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </button>
          </div>
          <router-link to="/" :class="{ active: isActive('/') }">Home</router-link>
          <a href="https://store.structurecity.com/" target="_blank">Marketplace</a>
          <router-link to="/listings" :class="{ active: isActive('/listings') }">Listings</router-link>
          <router-link to="/blog" :class="{ active: isActive('/blog') }">Blog</router-link>
          <router-link to="/services" :class="{ active: isActive('/services') }">Services</router-link>
          <router-link to="/about" :class="{ active: isActive('/about') }">About</router-link>
          <router-link to="/contact" :class="{ active: isActive('/contact') }">Contact</router-link>
          <div class="sc-nav__mobile-actions">
            <template v-if="isLoggedIn">
              <a href="/listing" class="btn-primary">Go to App</a>
            </template>
            <template v-else>
              <router-link to="/sign-in" class="btn-ghost">Log in</router-link>
              <router-link to="/register" class="btn-primary">Get started</router-link>
            </template>
          </div>
        </div>

        <!-- Search -->
        <div class="sc-nav__search" :class="{ 'sc-nav__search--open': searchOpen }" ref="searchWrap">
          <transition name="sc-search-expand">
            <input
              v-if="searchOpen"
              ref="searchInput"
              class="sc-nav__search-input"
              type="search"
              placeholder="Search listings, people, news…"
              v-model="searchQuery"
              @keydown.enter="doSearch"
              @keydown.esc="closeSearch"
            />
          </transition>
          <button
            class="sc-nav__search-btn"
            :class="{ 'sc-nav__search-btn--active': searchOpen }"
            @click.stop="toggleSearch"
            aria-label="Search"
          >
            <svg v-if="!searchOpen" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="sc-nav__actions">
          <template v-if="isLoggedIn">
            <a href="/listing" class="btn-primary">Go to App</a>
          </template>
          <template v-else>
            <router-link to="/sign-in" class="btn-ghost">Log in</router-link>
            <router-link to="/register" class="btn-primary">Get started</router-link>
          </template>
        </div>

        <button class="sc-nav__burger" @click="menuOpen = !menuOpen" aria-label="Menu">
          <span :class="{ open: menuOpen }"></span>
          <span :class="{ open: menuOpen }"></span>
          <span :class="{ open: menuOpen }"></span>
        </button>
      </div>
    </nav>

    <!-- ═══════ PAGE HERO ═══════ -->
    <div v-if="title || $slots['hero-extra']" class="sc-page-hero">
      <div class="sc-page-hero__dots"></div>
      <div class="sc-container sc-page-hero__content">
        <span v-if="badge" class="sc-hero__badge">{{ badge }}</span>
        <h1 v-if="title">{{ title }}</h1>
        <p v-if="subtitle">{{ subtitle }}</p>
        <slot name="hero-extra" />
      </div>
    </div>

    <!-- ═══════ CONTENT ═══════ -->
    <slot />

    <!-- ═══════ FOOTER ═══════ -->
    <footer class="sc-footer" style="margin-top: 50px;">
      <div class="sc-container">
        <div class="sc-footer__top">
          <div class="sc-footer__brand">
            <div class="sc-footer__brand-logo">
              <div v-if="company && company.logo" class="footer-logo-circle">
                <img :src="imgUrl + 'logo/' + company.logo" :alt="(company && company.name) || 'Structurecity'" />
              </div>
              <span class="sc-nav__logo-text" style="font-size:1.5rem;">Structure<span>city</span></span>
            </div>
            <p>Africa's premier construction and real estate social platform. Connect, discover, and grow.</p>
          </div>
          <div class="sc-footer__links">
            <div>
              <h4>Platform</h4>
              <router-link to="/listings">Browse Listings</router-link>
              <router-link to="/services">Services</router-link>
              <router-link to="/blog">Blog</router-link>
              <a href="https://store.structurecity.com/" target="_blank">Marketplace</a>
            </div>
            <div>
              <h4>Community</h4>
              <router-link to="/listing">My Feed</router-link>
              <router-link to="/about">About Us</router-link>
              <router-link to="/contact">Contact</router-link>
            </div>
            <div>
              <h4>Legal</h4>
              <router-link to="/terms-and-conditions">Terms &amp; Conditions</router-link>
              <router-link to="/privacy-policy">Privacy Policy</router-link>
            </div>
          </div>
        </div>
        <div class="sc-footer__bottom">
          <span>&copy; {{ currentYear }} Structurecity. All rights reserved.</span>
          <span v-if="company && company.email">{{ company.email }}</span>
        </div>
        <div class="sc-footer__credit">
          Developed &amp; managed by <a href="https://www.essyp.com" target="_blank" rel="noopener">Essyp</a>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import axios from 'axios';
export default {
  name: 'PublicLayout',
  props: {
    title:    { type: String, default: '' },
    subtitle: { type: String, default: '' },
    badge:    { type: String, default: '' },
  },
  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      apiUrl: process.env.VUE_APP_API_URL || '/api/',
      scrolled:    false,
      menuOpen:    false,
      searchOpen:  false,
      searchQuery: '',
      company:     null,
      currentYear: new Date().getFullYear(),
    };
  },
  computed: {
    isLoggedIn() { return !!localStorage.getItem('user'); },
    currentPath() { return (this.$route && this.$route.path) || window.location.pathname; },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('click', this.onDocClick);
    this.loadCompany();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    onScroll() { this.scrolled = window.scrollY > 60; },
    isActive(path) {
      if (path === '/') return this.currentPath === '/';
      return this.currentPath.startsWith(path);
    },
    loadCompany() {
      axios(this.apiUrl + 'company').then(res => {
        if (res && res.data) this.company = res.data;
      }).catch(() => {});
    },
    onDocClick(e) {
      if (this.$refs.searchWrap && !this.$refs.searchWrap.contains(e.target)) {
        this.closeSearch();
      }
    },
    toggleSearch() {
      if (this.searchOpen) {
        this.doSearch();
      } else {
        this.searchOpen = true;
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      }
    },
    closeSearch() {
      this.searchOpen = false;
      this.searchQuery = '';
    },
    doSearch() {
      const q = this.searchQuery.trim();
      if (!q) { this.closeSearch(); return; }
      this.$router.push({ path: '/search', query: { q } });
      this.closeSearch();
      this.menuOpen = false;
    },
  },
};
</script>

<style scoped>
/* ── Nav ─────────────────────────────────────────────── */
.sc-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid transparent;
  transition: border-color .2s, box-shadow .2s;
}
.sc-nav--scrolled { border-bottom-color: var(--border); box-shadow: var(--shadow-sm); }
.sc-nav__inner { display: flex; align-items: center; gap: 1.5rem; height: 68px; }
.sc-nav__logo { display: flex; align-items: center; text-decoration: none; }
.sc-nav__logo img { height: 36px; object-fit: contain; }
.sc-nav__logo-text {
  font-size: 1.375rem; font-weight: 800; color: var(--primary);
  text-decoration: none; letter-spacing: -.03em;
}
.sc-nav__logo-text span { color: var(--accent); }
.sc-nav__links { display: flex; align-items: center; gap: .125rem; margin-left: auto; }
.sc-nav__links a {
  padding: .375rem .875rem; font-size: .9375rem; font-weight: 500;
  color: var(--text-muted); text-decoration: none; border-radius: var(--radius-full);
  transition: color .15s, background .15s;
}
.sc-nav__links a:hover { color: var(--text); background: var(--surface-3); }
.sc-nav__links a.active {
  color: var(--primary);
  font-weight: 600;
  background: rgba(30,58,95,.07);
  position: relative;
}
.sc-nav__links a.active::after {
  content: '';
  position: absolute;
  bottom: 4px; left: 50%;
  transform: translateX(-50%);
  width: 4px; height: 4px;
  border-radius: 50%;
  background: var(--accent);
}
/* Search */
.sc-nav__search {
  display: flex; align-items: center; gap: .25rem;
}
.sc-nav__search-input {
  padding: .375rem .875rem;
  border: 1.5px solid var(--border);
  border-radius: var(--radius-full);
  font-size: .875rem; outline: none;
  background: var(--surface-2);
  color: var(--text); font-family: inherit;
  width: 220px;
  transition: border-color .15s;
}
.sc-nav__search-input:focus { border-color: var(--primary); background: #fff; }
.sc-nav__search-input::placeholder { color: var(--text-muted); }
.sc-nav__search-btn {
  width: 36px; height: 36px; border-radius: 50%;
  border: none; background: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-muted);
  transition: background .15s, color .15s;
  flex-shrink: 0;
}
.sc-nav__search-btn svg { pointer-events: none; }
.sc-nav__search-btn:hover,
.sc-nav__search-btn--active { background: var(--surface-3); color: var(--primary); }
.sc-search-expand-enter-active, .sc-search-expand-leave-active { transition: width .25s ease, opacity .2s; }
.sc-search-expand-enter-from, .sc-search-expand-leave-to { width: 0; opacity: 0; }

/* Mobile search (inside the open mobile menu) */
.sc-nav__mobile-search { display: none; }

.sc-nav__actions { display: flex; align-items: center; gap: .5rem; }
.sc-nav__burger {
  display: none; flex-direction: column; gap: 5px;
  padding: .5rem; background: none; border: none; cursor: pointer; margin-left: auto;
}
.sc-nav__burger span {
  display: block; width: 22px; height: 2px; background: var(--text);
  border-radius: 2px; transition: transform .2s, opacity .2s;
}
.sc-nav__burger span.open:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.sc-nav__burger span.open:nth-child(2) { opacity: 0; }
.sc-nav__burger span.open:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* ── Page Hero ───────────────────────────────────────── */
.sc-page-hero {
  position: relative; padding: 8rem 1.5rem 5rem;
  background: linear-gradient(135deg, #0d1f36 0%, var(--primary) 55%, #1a4a7a 100%);
  overflow: hidden;
}
.sc-page-hero__dots {
  position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.06) 1px, transparent 1px);
  background-size: 40px 40px;
}
.sc-page-hero::after {
  content: ''; position: absolute; bottom: -1px; left: 0; right: 0;
  height: 60px; background: var(--surface-2);
  clip-path: ellipse(60% 100% at 50% 100%);
}
.sc-page-hero__content {
  position: relative; z-index: 2; text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 1rem;
}
.sc-page-hero__content h1 {
  font-size: clamp(2rem, 4vw, 3rem); font-weight: 800;
  color: #fff; line-height: 1.1; letter-spacing: -.03em; margin: 0;
}
.sc-page-hero__content p {
  font-size: 1.0625rem; color: rgba(255,255,255,.72);
  max-width: 560px; line-height: 1.65; margin: 0;
}
.sc-hero__badge {
  display: inline-block; padding: .375rem 1rem;
  background: rgba(245,158,11,.15); border: 1px solid rgba(245,158,11,.4);
  border-radius: var(--radius-full); color: var(--accent);
  font-size: .8125rem; font-weight: 600; letter-spacing: .04em; text-transform: uppercase;
}

/* ── Footer ──────────────────────────────────────────── */
.sc-footer { background: #0d1f36; color: rgba(255,255,255,.65); padding: 4rem 0 2rem; }
.sc-footer__top {
  display: grid; grid-template-columns: 1.5fr 2fr; gap: 3rem;
  padding-bottom: 3rem; border-bottom: 1px solid rgba(255,255,255,.08);
}
.sc-footer__brand-logo { display: flex; align-items: center; gap: .75rem; }
.footer-logo-circle {
  width: 54px; height: 54px; border-radius: 50%;
  background: #fff; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; padding: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
}
.footer-logo-circle img { width: 100%; height: 100%; object-fit: contain; }
.sc-footer__brand p { margin: 1rem 0 0; font-size: .9rem; line-height: 1.65; max-width: 280px; }
.sc-footer__links { display: grid; grid-template-columns: repeat(3,1fr); gap: 2rem; }
.sc-footer__links h4 {
  color: #fff; font-size: .875rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: .06em; margin: 0 0 1rem;
}
.sc-footer__links a {
  display: block; color: rgba(255,255,255,.5); text-decoration: none;
  font-size: .9rem; margin-bottom: .625rem; transition: color .15s;
}
.sc-footer__links a:hover { color: #fff; }
.sc-footer__bottom {
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: .5rem;
  padding-top: 2rem; font-size: .8125rem; color: rgba(255,255,255,.3);
}
.sc-footer__credit {
  text-align: center; padding-top: .75rem;
  font-size: .75rem; color: rgba(255,255,255,.25);
}
.sc-footer__credit a { color: rgba(255,255,255,.45); text-decoration: none; }
.sc-footer__credit a:hover { color: #fff; }


/* ── Responsive ──────────────────────────────────────── */
.sc-nav__mobile-actions { display: none; }
.sc-nav__mobile-search { display: none; }
@media (max-width: 900px) {
  .sc-nav__links, .sc-nav__actions { display: none; }
  .sc-nav__search-input { width: min(170px, calc(100vw - 200px)); }
  .sc-nav__burger { display: flex; }
  .sc-nav__links--open {
    display: flex; flex-direction: column; align-items: flex-start;
    position: fixed; top: 68px; left: 0; right: 0;
    background: var(--surface); padding: 0 0 1rem;
    box-shadow: var(--shadow-lg); border-bottom: 1px solid var(--border); gap: .25rem; z-index: 999;
  }
  .sc-nav__links--open a { padding: .75rem 1.5rem; width: 100%; box-sizing: border-box; }
  .sc-nav__mobile-actions {
    display: flex; gap: .5rem; padding: .75rem 1.5rem 0; margin-top: .25rem;
    border-top: 1px solid var(--border); width: 100%; box-sizing: border-box;
  }
  .sc-nav__mobile-search {
    display: flex; align-items: center; gap: .5rem;
    padding: .875rem 1.5rem;
    border-bottom: 1px solid var(--border);
    width: 100%; box-sizing: border-box;
  }
  .sc-mobile__search-input {
    flex: 1; padding: .5rem .875rem;
    border: 1.5px solid var(--border);
    border-radius: var(--radius-full); font-size: .875rem;
    outline: none; background: var(--surface-2);
    color: var(--text); font-family: inherit;
  }
  .sc-mobile__search-input:focus { border-color: var(--primary); }
  .sc-mobile__search-btn {
    width: 34px; height: 34px; border-radius: 50%;
    border: none; background: var(--primary);
    color: #fff; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
}
@media (max-width: 768px) {
  .sc-page-hero { padding: 7rem 1.25rem 4rem; }
  .sc-footer__top { grid-template-columns: 1fr; }
  .sc-footer__links { grid-template-columns: 1fr 1fr; }
  .sc-field-row { grid-template-columns: 1fr; }
}
</style>
